import { useState } from "react";
import { allVenues } from "../../../mocks/allVenues";
import { venueCoords } from "../../../mocks/venueCoords";
import type { RouteStop } from "../../../hooks/useMyRoutes";

interface Props {
  onClose: () => void;
  onSave: (name: string, stops: Omit<RouteStop, "completed" | "completedAt">[]) => void;
}

const CATEGORIES = [
  "Tümü", "Tarihi Yerler", "Lezzet Durakları", "Eğlence",
  "Konaklama", "Doğa ve Rotalar", "Gezilecek Yerler",
];

export default function RouteBuilderModal({ onClose, onSave }: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [routeName, setRouteName] = useState("");
  const [catFilter, setCatFilter] = useState("Tümü");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const filtered = allVenues.filter((v) => {
    const matchCat = catFilter === "Tümü" || v.category === catFilter;
    const matchSearch = v.name.toLowerCase().includes(search.toLowerCase()) ||
      v.district.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSave = () => {
    const stops: Omit<RouteStop, "completed" | "completedAt">[] = selected
      .map((id) => {
        const v = allVenues.find((x) => x.id === id);
        const c = venueCoords.find((x) => x.id === id);
        if (!v || !c) return null;
        return {
          venueId: v.id,
          venueName: v.name,
          category: v.category,
          categorySlug: v.categorySlug,
          address: v.address,
          district: v.district,
          image: v.image,
          lat: c.lat,
          lng: c.lng,
        };
      })
      .filter(Boolean) as Omit<RouteStop, "completed" | "completedAt">[];
    onSave(routeName, stops);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h2 className="font-bold text-stone-900 text-lg">Yeni Rota Oluştur</h2>
            <p className="text-xs text-stone-500">Adım {step}/2</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-2">Rota Adı *</label>
              <input
                value={routeName}
                onChange={(e) => setRouteName(e.target.value)}
                placeholder="Örn: Tarihi Bursa Turu, Lezzet Rotası..."
                className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-emerald-400"
              />
              <p className="text-xs text-stone-400 mt-2">Rotanıza anlamlı bir isim verin.</p>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-sm text-stone-600 mb-4">
                Rotanıza eklemek istediğiniz mekanları seçin. <span className="font-semibold text-emerald-700">{selected.length} mekan seçildi.</span>
              </p>
              {/* Search & filter */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Mekan ara..."
                    className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {CATEGORIES.map((c) => (
                  <button key={c} onClick={() => setCatFilter(c)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${catFilter === c ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                    {c}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                {filtered.slice(0, 20).map((v) => {
                  const isSelected = selected.includes(v.id);
                  return (
                    <div key={v.id} onClick={() => toggle(v.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? "border-emerald-400 bg-emerald-50" : "border-stone-100 hover:border-stone-300 bg-white"}`}>
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={v.image} alt={v.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-stone-800 text-sm truncate">{v.name}</p>
                        <p className="text-xs text-stone-500">{v.category} · {v.district}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "border-emerald-500 bg-emerald-500" : "border-stone-300"}`}>
                        {isSelected && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-100 flex items-center justify-between">
          {step === 2 ? (
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-stone-600 hover:text-stone-800 cursor-pointer whitespace-nowrap flex items-center gap-1">
              <i className="ri-arrow-left-line" /> Geri
            </button>
          ) : <div />}
          {step === 1 ? (
            <button
              disabled={!routeName.trim()}
              onClick={() => setStep(2)}
              className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              Mekan Seç →
            </button>
          ) : (
            <button
              disabled={selected.length === 0}
              onClick={handleSave}
              className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              Rotayı Kaydet ({selected.length})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
